/**
 * LyteWork Order Packet v1.0
 * ----------------------------------------------------------------------------
 * ONE standard way for every customizer to send a design to Shopify.
 *
 * What it guarantees on every order:
 *   - Text, fonts and options arrive as VISIBLE properties (no underscores)
 *   - Logos and the preview arrive as REAL FILES. Shopify hosts them and the
 *     order shows clickable links (Logo, Preview) instead of hidden image code
 *   - The full design is kept in a hidden _lw_design JSON for future re-edits
 *   - Surcharges go on the "Customization Options" line, linked by _lw_link_id
 *     (works with lytework-cart-count-fix.js), with rollback if that fails
 *
 * Load it once, before any customizer (theme.liquid, near the shared snippet):
 *   <script src="{{ 'lytework-order-packet.js' | asset_url }}" defer></script>
 *
 * Usage:
 *   await window.LyteWorkOrder.add({
 *     variantId: 123,
 *     quantity: 2,
 *     title: 'Diamond Series Clear Acrylic',          // used on the surcharge line
 *     properties: { 'Recipient': 'Angie Carr', 'Fonts': '...' },  // visible, in order
 *     files: { 'Logo': logoDataUrlOrFile, 'Preview': previewDataUrl },
 *     design: { ...anything needed to rebuild the design... },
 *     surcharges: [{ label: 'Back Engraving', cents: 500 }]      // per item
 *   });
 * ----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var SURCHARGE_VARIANT_ID = 41832906817610; // "Customization Options" ($0.01, qty = cents)
  var MAX_FILE_BYTES = 10 * 1024 * 1024;
  var EXT = {
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'application/pdf': 'pdf'
  };

  function linkId() {
    return 'lw_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  }

  /** "'Monotype Corsiva', cursive" -> "Monotype Corsiva" */
  function fontName(css) {
    if (!css) return '';
    return String(css).split(',')[0].replace(/['"]/g, '').trim();
  }

  function slug(s) {
    return String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'file';
  }

  function dataUrlToBlob(dataUrl) {
    var comma = dataUrl.indexOf(',');
    if (comma < 0) throw new Error('Unreadable image data. Please re-upload it.');
    var header = dataUrl.slice(0, comma);
    var mime = (header.match(/^data:([^;,]+)/) || [])[1] || 'application/octet-stream';
    var body = dataUrl.slice(comma + 1);
    var bytes;
    if (header.indexOf(';base64') !== -1) {
      var bin = atob(body);
      bytes = new Uint8Array(bin.length);
      for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    } else {
      bytes = new TextEncoder().encode(decodeURIComponent(body));
    }
    return new Blob([bytes], { type: mime });
  }

  /** Accepts a data URL, a Blob/File, or a plain URL (sent as text). */
  function appendFile(fd, name, src) {
    if (!src) return;
    if (typeof src === 'string' && src.indexOf('data:') !== 0) {
      fd.append('properties[' + name + ']', src); // already hosted, e.g. clipart/template URL
      return;
    }
    var blob = typeof src === 'string' ? dataUrlToBlob(src) : src;
    if (!blob.size) throw new Error(name + ' is empty. Please re-upload it.');
    if (blob.size > MAX_FILE_BYTES) throw new Error(name + ' is too large (10MB max).');
    var ext = EXT[blob.type] || (blob.name && blob.name.split('.').pop()) || 'bin';
    var filename = blob.name || slug(name) + '-' + Date.now() + '.' + ext;
    fd.append('properties[' + name + ']', blob, filename);
  }

  async function post(fd) {
    var res = await fetch('/cart/add.js', {
      method: 'POST',
      body: fd,
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
    });
    var data = null;
    try { data = await res.json(); } catch (e) { /* non-JSON response */ }
    if (!res.ok) {
      throw new Error((data && (data.description || data.message)) || 'The cart rejected this item.');
    }
    return data;
  }

  async function removeLine(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
    } catch (e) {
      console.error('LyteWorkOrder: rollback failed', e);
    }
  }

  async function add(opts) {
    opts = opts || {};
    if (!opts.variantId) throw new Error('Product variant not found. Please refresh and try again.');

    var qty = Math.max(1, parseInt(opts.quantity, 10) || 1);
    var id = linkId();
    var title = opts.title || 'Product';

    // ---- Main product line ----
    var fd = new FormData();
    fd.append('id', String(opts.variantId));
    fd.append('quantity', String(qty));

    Object.keys(opts.properties || {}).forEach(function (key) {
      var val = opts.properties[key];
      if (val === undefined || val === null || String(val).trim() === '') return;
      fd.append('properties[' + key + ']', String(val));
    });

    Object.keys(opts.files || {}).forEach(function (key) {
      appendFile(fd, key, opts.files[key]);
    });

    fd.append('properties[_lw_link_id]', id);
    if (opts.design) {
      fd.append('properties[_lw_design]', JSON.stringify(opts.design));
    }

    var item = await post(fd);

    // ---- Surcharge line (Customization Options) ----
    var charges = (opts.surcharges || []).filter(function (s) { return s && s.cents > 0; });
    var totalCents = charges.reduce(function (sum, s) {
      return sum + Math.round(s.cents) * (s.perItem === false ? 1 : qty);
    }, 0);

    if (totalCents > 0) {
      var sfd = new FormData();
      sfd.append('id', String(SURCHARGE_VARIANT_ID));
      sfd.append('quantity', String(totalCents));
      sfd.append('properties[For]', qty > 1 ? title + ' (' + qty + ')' : title);
      charges.forEach(function (s) {
        sfd.append(
          'properties[' + s.label + ']',
          '$' + (s.cents / 100).toFixed(2) + (s.perItem === false ? '' : '/ea')
        );
      });
      sfd.append('properties[_lw_link_id]', id);

      try {
        await post(sfd);
      } catch (err) {
        console.error('LyteWorkOrder: surcharge failed, rolling back', err);
        if (item && item.key) await removeLine(item.key);
        throw new Error("We couldn't add the customization charges, so nothing was added. Please try again.");
      }
    }

    return item;
  }

  window.LyteWorkOrder = {
    version: '1.0',
    add: add,
    fontName: fontName,
    dataUrlToBlob: dataUrlToBlob,
    linkId: linkId
  };
})();
